// Lab 2: primary/backup replication with view changes.
// Two coupled protocols: the view service numbers (primary, backup)
// configurations, and replicas transfer state, order operations, and answer
// clients only in an active view. Every operation, including reads, is
// forwarded to the backup before the client is answered.
//
// config: { viewServer, servers, clients, linkDelay=1, pingInterval=3,
//           checkInterval=4, opTimeout=5, transferTimeout=5, clientRetry=6 }
import { EventKind } from '../../core/types';
import { amoApply } from '../common/amo';
import { createKvStore, kvApply } from '../common/kvStore';
import { checkLab2Invariants } from './invariants';

export const MSG_PING = 'ping';
export const MSG_VIEW = 'view';
export const MSG_VIEW_QUERY = 'viewquery';
export const MSG_TRANSFER = 'transfer';
export const MSG_TRANSFER_REPLY = 'transferreply';
export const MSG_FORWARD = 'forward';
export const MSG_FORWARD_REPLY = 'forwardreply';
export const MSG_CREQUEST = 'crequest';
export const MSG_CREPLY = 'creply';
export const MSG_STALE = 'stale';

export const TIMER_PING = 'ping';
export const TIMER_CHECK = 'check';
export const TIMER_OP = 'op';
export const TIMER_TRANSFER = 'transfer';
export const TIMER_RETRY = 'retry';

export const labMeta = {
  id: 'lab2',
  title: 'Lab 2 · Primary/backup',
  description: 'A view service numbers configurations while replicas transfer state and order every operation.',
};

export function defaultConfig() {
  return {
    viewServer: 'V',
    servers: ['S1', 'S2', 'S3'],
    clients: ['C1'],
    linkDelay: 1,
    pingInterval: 3,
    checkInterval: 4,
    opTimeout: 5,
    transferTimeout: 5,
    clientRetry: 6,
  };
}

function emptyView() {
  return { n: 0, primary: null, backup: null };
}

export function initialize(config) {
  const nodes = {};
  nodes[config.viewServer] = {
    durable: {
      role: 'viewserver',
      current: emptyView(),
      pending: null,
      lastPing: {},
      history: [],
    },
    volatile: {},
  };
  config.servers.forEach((id) => {
    nodes[id] = {
      durable: {
        role: 'pbserver',
        view: emptyView(),
        activeN: 0,
        status: 'idle',
        kv: createKvStore(),
        amo: {},
        appliedIds: [],
        opnum: 0,
        pendingOp: null,
        served: [],
      },
      volatile: {},
    };
  });
  config.clients.forEach((id) => {
    nodes[id] = {
      durable: {
        role: 'pbclient',
        view: emptyView(),
        nextSeq: 1,
        accepted: 0,
        sentIds: [],
        completed: [],
      },
      volatile: { outstanding: null, held: null, retries: 0 },
    };
  });
  const initialEvents = config.servers.map((id, i) => ({
    kind: EventKind.TIMER,
    from: id,
    to: id,
    payload: { timerKey: TIMER_PING, timerType: TIMER_PING },
    createdAt: 0,
    deliverAt: i + 1,
    parentId: null,
    label: 'ping timer',
  }));
  initialEvents.push({
    kind: EventKind.TIMER,
    from: config.viewServer,
    to: config.viewServer,
    payload: { timerKey: TIMER_CHECK, timerType: TIMER_CHECK },
    createdAt: 0,
    deliverAt: 2,
    parentId: null,
    label: 'check timer',
  });
  return { nodes, initialEvents };
}

export function freshVolatile(durable) {
  if (durable.role === 'pbclient') return { outstanding: null, held: null, retries: 0 };
  return {};
}

function clone(part) {
  return JSON.parse(JSON.stringify(part));
}

function send(to, payload, delay, label) {
  return { to, payload, delay, label };
}

// ---------------- ViewServer ----------------

function viewServerPing(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const { server, viewN } = event.payload;
  durable.lastPing[server] = state.virtualTime;
  if (
    durable.pending &&
    server === durable.pending.primary &&
    viewN === durable.pending.n
  ) {
    durable.current = { ...durable.pending };
    durable.pending = null;
  }
  return {
    durable,
    outbox: [
      send(
        event.from,
        { msgType: MSG_VIEW, view: { ...durable.pending || durable.current } },
        config.linkDelay,
        'view update'
      ),
    ],
    note: 'viewserver hears ping from ' + server,
  };
}

function viewServerQuery(state, config, nodeId, event) {
  return {
    outbox: [
      send(
        event.from,
        { msgType: MSG_VIEW, view: { ...state.nodes[nodeId].durable.current } },
        config.linkDelay,
        'view reply'
      ),
    ],
    note: 'viewserver answers view query',
  };
}

function viewServerCheck(state, config, nodeId) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const now = state.virtualTime;
  const live = (id) =>
    durable.lastPing[id] !== undefined &&
    now - durable.lastPing[id] <= config.checkInterval * 2;
  const liveServers = config.servers.filter(live);
  let note = 'check: no view change';
  if (durable.current.n === 0 && !durable.pending) {
    if (liveServers.length > 0) {
      durable.pending = { n: 1, primary: liveServers[0], backup: null };
      durable.history.push({ ...durable.pending });
      note = 'check: propose view 1 with ' + liveServers[0];
    }
  } else if (!durable.pending) {
    const cur = durable.current;
    const primaryLive = cur.primary && live(cur.primary);
    const backupLive = cur.backup && live(cur.backup);
    const idle = liveServers.filter((s) => s !== cur.primary && s !== cur.backup);
    if (!primaryLive && cur.backup && backupLive) {
      durable.pending = {
        n: cur.n + 1,
        primary: cur.backup,
        backup: idle[0] || null,
      };
      durable.history.push({ ...durable.pending });
      note = 'check: promote ' + cur.backup + ' to view ' + durable.pending.n;
    } else if (!primaryLive && (!cur.backup || !backupLive)) {
      note = 'check: primary gone, no live backup; views stuck';
    } else if (primaryLive && (!cur.backup || !backupLive)) {
      durable.pending = {
        n: cur.n + 1,
        primary: cur.primary,
        backup: idle[0] || null,
      };
      durable.history.push({ ...durable.pending });
      note = 'check: view ' + durable.pending.n + ' refreshes backup slot';
    }
  } else {
    note = 'check: waiting for view ' + durable.pending.n + ' ack';
  }
  return {
    durable,
    timerOps: [
      {
        action: 'set',
        timerKey: TIMER_CHECK,
        timerType: TIMER_CHECK,
        payload: {},
        delay: config.checkInterval,
        label: 'check timer',
      },
    ],
    note,
  };
}

// ---------------- PBServer ----------------

function adoptPrimaryNoBackup(durable, view) {
  durable.view = { ...view };
  durable.status = 'active';
  durable.activeN = view.n;
}

function serverViewUpdate(state, config, nodeId, view) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  if (view.n <= durable.activeN) {
    return { durable, note: 'stale view ignored' };
  }
  if (view.n <= durable.view.n && durable.status !== 'idle') {
    return { durable, note: 'stale view ignored' };
  }
  if (view.primary === nodeId) {
    durable.view = { ...view };
    if (view.backup) {
      durable.status = 'pending';
      return {
        durable,
        outbox: [
          send(
            view.backup,
            {
              msgType: MSG_TRANSFER,
              view: view.n,
              kv: { ...durable.kv },
              amo: JSON.parse(JSON.stringify(durable.amo)),
              appliedIds: [...durable.appliedIds],
              opnum: durable.opnum,
            },
            config.linkDelay,
            'state transfer'
          ),
        ],
        timerOps: [
          {
            action: 'set',
            timerKey: TIMER_TRANSFER,
            timerType: TIMER_TRANSFER,
            payload: { view: view.n },
            delay: config.transferTimeout,
            label: 'transfer timer',
          },
        ],
        note: 'primary pauses for state transfer in view ' + view.n,
      };
    }
    adoptPrimaryNoBackup(durable, view);
    return { durable, note: 'primary adopts solo view ' + view.n };
  }
  if (view.backup === nodeId) {
    durable.view = { ...view };
    durable.status = 'pending';
    return { durable, note: 'backup waits for state transfer' };
  }
  durable.view = { ...view };
  durable.status = 'idle';
  durable.pendingOp = null;
  return { durable, note: 'steps down to idle in view ' + view.n };
}

function serverTransfer(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  if (
    durable.status !== 'pending' ||
    durable.view.backup !== nodeId ||
    p.view !== durable.view.n ||
    event.from !== durable.view.primary
  ) {
    return { durable, note: 'unexpected transfer ignored' };
  }
  durable.kv = { ...p.kv };
  durable.amo = JSON.parse(JSON.stringify(p.amo));
  durable.appliedIds = [...p.appliedIds];
  durable.opnum = p.opnum;
  durable.pendingOp = null;
  durable.status = 'active';
  durable.activeN = durable.view.n;
  return {
    durable,
    outbox: [
      send(
        event.from,
        { msgType: MSG_TRANSFER_REPLY, view: durable.view.n },
        config.linkDelay,
        'transfer reply'
      ),
    ],
    note: 'backup installs view ' + durable.view.n + ' state',
  };
}

function serverTransferReply(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  if (
    durable.status !== 'pending' ||
    durable.view.primary !== nodeId ||
    p.view !== durable.view.n ||
    event.from !== durable.view.backup
  ) {
    return { durable, note: 'unexpected transfer reply ignored' };
  }
  durable.status = 'active';
  durable.activeN = durable.view.n;
  return {
    durable,
    timerOps: [{ action: 'cancel', timerKey: TIMER_TRANSFER }],
    note: 'primary activates view ' + durable.view.n,
  };
}

function staleReply(to, view, delay) {
  return send(to, { msgType: MSG_STALE, view: { ...view } }, delay, 'stale view');
}

function serverClientRequest(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  const activePrimary =
    durable.status === 'active' && durable.view.primary === nodeId;
  if (!activePrimary || p.view !== durable.view.n) {
    return {
      durable,
      outbox: [staleReply(event.from, durable.view, config.linkDelay)],
      note: 'not the serving primary; redirect client',
    };
  }
  durable.opnum += 1;
  const op = {
    opnum: durable.opnum,
    view: durable.view.n,
    client: p.client,
    seq: p.seq,
    command: p.command,
  };
  durable.pendingOp = { ...op };
  if (!durable.view.backup) {
    const outcome = amoApply(durable.amo, p.client, p.seq, () => {
      durable.appliedIds.push(p.client + '#' + p.seq);
      return kvApply(durable.kv, p.command);
    });
    durable.pendingOp = null;
    durable.served.push({ view: durable.view.n, opnum: op.opnum });
    return {
      durable,
      outbox: [
        send(
          p.client,
          { msgType: MSG_CREPLY, client: p.client, seq: p.seq, result: outcome.result },
          config.linkDelay,
          'client reply'
        ),
      ],
      note: 'solo primary executes op ' + op.opnum,
    };
  }
  return {
    durable,
    outbox: [
      send(
        durable.view.backup,
        {
          msgType: MSG_FORWARD,
          view: durable.view.n,
          opnum: op.opnum,
          client: p.client,
          seq: p.seq,
          command: p.command,
        },
        config.linkDelay,
        'forward op ' + op.opnum
      ),
    ],
    timerOps: [
      {
        action: 'set',
        timerKey: TIMER_OP,
        timerType: TIMER_OP,
        payload: { view: durable.view.n, opnum: op.opnum },
        delay: config.opTimeout,
        label: 'op timer',
      },
    ],
    note: 'primary forwards op ' + op.opnum,
  };
}

function serverForward(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  if (
    durable.status !== 'active' ||
    durable.view.backup !== nodeId ||
    event.from !== durable.view.primary ||
    p.view !== durable.view.n
  ) {
    return { durable, note: 'stale-role forward ignored' };
  }
  amoApply(durable.amo, p.client, p.seq, () => {
    durable.appliedIds.push(p.client + '#' + p.seq);
    return kvApply(durable.kv, p.command);
  });
  durable.opnum = Math.max(durable.opnum, p.opnum);
  return {
    durable,
    outbox: [
      send(
        event.from,
        { msgType: MSG_FORWARD_REPLY, view: p.view, opnum: p.opnum },
        config.linkDelay,
        'forward reply'
      ),
    ],
    note: 'backup mirrors op ' + p.opnum,
  };
}

function serverForwardReply(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const p = event.payload;
  const op = durable.pendingOp;
  if (
    !op ||
    durable.status !== 'active' ||
    p.view !== op.view ||
    p.opnum !== op.opnum ||
    event.from !== durable.view.backup
  ) {
    return { durable, note: 'unexpected forward reply ignored' };
  }
  const outcome = amoApply(durable.amo, op.client, op.seq, () => {
    durable.appliedIds.push(op.client + '#' + op.seq);
    return kvApply(durable.kv, op.command);
  });
  durable.pendingOp = null;
  durable.served.push({ view: op.view, opnum: op.opnum });
  return {
    durable,
    timerOps: [{ action: 'cancel', timerKey: TIMER_OP }],
    outbox: [
      send(
        op.client,
        { msgType: MSG_CREPLY, client: op.client, seq: op.seq, result: outcome.result },
        config.linkDelay,
        'client reply'
      ),
    ],
    note: 'primary completes op ' + op.opnum,
  };
}

function serverPingTimer(state, config, nodeId) {
  const node = state.nodes[nodeId];
  return {
    outbox: [
      send(
        config.viewServer,
        { msgType: MSG_PING, server: nodeId, viewN: node.durable.activeN },
        config.linkDelay,
        'ping'
      ),
    ],
    timerOps: [
      {
        action: 'set',
        timerKey: TIMER_PING,
        timerType: TIMER_PING,
        payload: {},
        delay: config.pingInterval,
        label: 'ping timer',
      },
    ],
    note: nodeId + ' pings viewserver',
  };
}

function serverOpTimer(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const op = durable.pendingOp;
  if (
    !op ||
    op.view !== event.payload.view ||
    op.opnum !== event.payload.opnum ||
    !durable.view.backup
  ) {
    return { durable, note: 'stale op timer discarded' };
  }
  return {
    durable,
    outbox: [
      send(
        durable.view.backup,
        {
          msgType: MSG_FORWARD,
          view: op.view,
          opnum: op.opnum,
          client: op.client,
          seq: op.seq,
          command: op.command,
        },
        config.linkDelay,
        'forward op ' + op.opnum + ' (retry)'
      ),
    ],
    timerOps: [
      {
        action: 'set',
        timerKey: TIMER_OP,
        timerType: TIMER_OP,
        payload: { view: op.view, opnum: op.opnum },
        delay: config.opTimeout,
        label: 'op timer',
      },
    ],
    note: 'primary retries forward of op ' + op.opnum,
  };
}

function serverTransferTimer(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  if (
    durable.status !== 'pending' ||
    event.payload.view !== durable.view.n ||
    !durable.view.backup
  ) {
    return { durable, note: 'stale transfer timer discarded' };
  }
  return {
    durable,
    outbox: [
      send(
        durable.view.backup,
        {
          msgType: MSG_TRANSFER,
          view: durable.view.n,
          kv: { ...durable.kv },
          amo: JSON.parse(JSON.stringify(durable.amo)),
          appliedIds: [...durable.appliedIds],
          opnum: durable.opnum,
        },
        config.linkDelay,
        'state transfer (retry)'
      ),
    ],
    timerOps: [
      {
        action: 'set',
        timerKey: TIMER_TRANSFER,
        timerType: TIMER_TRANSFER,
        payload: { view: durable.view.n },
        delay: config.transferTimeout,
        label: 'transfer timer',
      },
    ],
    note: 'primary retries state transfer',
  };
}

// ---------------- PBClient ----------------

export function handleMessage(state, nodeId, event) {
  const config = state.config;
  const node = state.nodes[nodeId];
  if (!node) return { note: 'message to unknown node; ignored' };
  const role = node.durable.role;
  const t = event.payload.msgType;
  if (role === 'viewserver') {
    if (t === MSG_PING) return viewServerPing(state, config, nodeId, event);
    if (t === MSG_VIEW_QUERY) return viewServerQuery(state, config, nodeId, event);
    return {};
  }
  if (role === 'pbserver') {
    if (t === MSG_VIEW) return serverViewUpdate(state, config, nodeId, event.payload.view);
    if (t === MSG_TRANSFER) return serverTransfer(state, config, nodeId, event);
    if (t === MSG_TRANSFER_REPLY) return serverTransferReply(state, config, nodeId, event);
    if (t === MSG_CREQUEST) return serverClientRequest(state, config, nodeId, event);
    if (t === MSG_FORWARD) return serverForward(state, config, nodeId, event);
    if (t === MSG_FORWARD_REPLY) return serverForwardReply(state, config, nodeId, event);
    return {};
  }
  if (role === 'pbclient') {
    return clientMessage(state, config, nodeId, event);
  }
  return {};
}

function clientMessage(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const p = event.payload;
  if (p.msgType === MSG_CREPLY) {
    if (volatile.outstanding && volatile.outstanding.seq === p.seq) {
      durable.completed.push({ seq: p.seq, result: p.result });
      volatile.outstanding = null;
      volatile.retries = 0;
      return {
        durable,
        volatile,
        timerOps: [{ action: 'cancel', timerKey: TIMER_RETRY + ':' + p.seq }],
        note: 'client completes seq ' + p.seq,
      };
    }
    return { durable, volatile, note: 'stale client reply ignored' };
  }
  if (p.msgType === MSG_VIEW) {
    durable.view = { ...p.view };
    volatile.retries = 0;
    if (volatile.held) {
      return clientFlushHeld(durable, volatile, nodeId, config);
    }
    if (volatile.outstanding && durable.view.primary) {
      return {
        durable,
        volatile,
        outbox: [
          send(
            durable.view.primary,
            {
              msgType: MSG_CREQUEST,
              view: durable.view.n,
              client: nodeId,
              seq: volatile.outstanding.seq,
              command: volatile.outstanding.command,
            },
            config.linkDelay,
            'client request (redirect)'
          ),
        ],
        note: 'client redirects to view ' + durable.view.n,
      };
    }
    return { durable, volatile, note: 'client caches view ' + durable.view.n };
  }
  if (p.msgType === MSG_STALE) {
    durable.view = { ...p.view };
    volatile.retries = 0;
    if (volatile.outstanding && durable.view.primary) {
      return {
        durable,
        volatile,
        outbox: [
          send(
            durable.view.primary,
            {
              msgType: MSG_CREQUEST,
              view: durable.view.n,
              client: nodeId,
              seq: volatile.outstanding.seq,
              command: volatile.outstanding.command,
            },
            config.linkDelay,
            'client request (redirect)'
          ),
        ],
        timerOps: [
          {
            action: 'set',
            timerKey: TIMER_RETRY + ':' + volatile.outstanding.seq,
            timerType: TIMER_RETRY,
            payload: { seq: volatile.outstanding.seq },
            delay: config.clientRetry,
            label: 'client retry',
          },
        ],
        note: 'client learns view ' + durable.view.n + ' and retries',
      };
    }
    return { durable, volatile, note: 'client caches view ' + durable.view.n };
  }
  return { durable, volatile, note: 'unexpected client message ignored' };
}

function clientFlushHeld(durable, volatile, nodeId, config) {
  const held = volatile.held;
  volatile.held = null;
  const seq = durable.nextSeq;
  durable.nextSeq += 1;
  durable.accepted += 1;
  durable.sentIds.push(nodeId + '#' + seq);
  volatile.outstanding = { seq, command: held.command };
  return {
    durable,
    volatile,
    outbox: [
      send(
        durable.view.primary,
        {
          msgType: MSG_CREQUEST,
          view: durable.view.n,
          client: nodeId,
          seq,
          command: held.command,
        },
        config.linkDelay,
        'client request'
      ),
    ],
    timerOps: [
      {
        action: 'set',
        timerKey: TIMER_RETRY + ':' + seq,
        timerType: TIMER_RETRY,
        payload: { seq },
        delay: config.clientRetry,
        label: 'client retry',
      },
    ],
    note: 'client sends seq ' + seq + ' in view ' + durable.view.n,
  };
}

export function handleClientCommand(state, nodeId, event) {
  const config = state.config;
  const node = state.nodes[nodeId];
  if (!node || node.durable.role !== 'pbclient') {
    return { note: 'invoke sent to non-client; ignored' };
  }
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  if (volatile.outstanding || volatile.held) {
    return { durable, volatile, note: 'client busy; command ignored' };
  }
  volatile.held = { command: event.payload.command };
  if (durable.view.primary) {
    return clientFlushHeld(durable, volatile, nodeId, config);
  }
  return {
    durable,
    volatile,
    outbox: [
      send(
        config.viewServer,
        { msgType: MSG_VIEW_QUERY },
        config.linkDelay,
        'view query'
      ),
    ],
    note: 'client queries viewserver',
  };
}

function clientRetry(state, config, nodeId, event) {
  const node = state.nodes[nodeId];
  const durable = clone(node.durable);
  const volatile = clone(node.volatile);
  const { seq } = event.payload;
  if (!volatile.outstanding || volatile.outstanding.seq !== seq) {
    return { durable, volatile, note: 'stale client timer discarded' };
  }
  volatile.retries = (volatile.retries || 0) + 1;
  const rearm = {
    action: 'set',
    timerKey: TIMER_RETRY + ':' + seq,
    timerType: TIMER_RETRY,
    payload: { seq },
    delay: config.clientRetry,
    label: 'client retry',
  };
  // Every third unanswered retry refreshes the cached view instead of
  // resending to a possibly dead primary.
  if (!durable.view.primary || volatile.retries % 3 === 0) {
    return {
      durable,
      volatile,
      outbox: [
        send(config.viewServer, { msgType: MSG_VIEW_QUERY }, config.linkDelay, 'view query'),
      ],
      timerOps: [rearm],
      note: 'client re-queries viewserver',
    };
  }
  return {
    durable,
    volatile,
    outbox: [
      send(
        durable.view.primary,
        {
          msgType: MSG_CREQUEST,
          view: durable.view.n,
          client: nodeId,
          seq,
          command: volatile.outstanding.command,
        },
        config.linkDelay,
        'client request (retry)'
      ),
    ],
    timerOps: [rearm],
    note: 'client retries seq ' + seq,
  };
}

export function handleTimer(state, nodeId, event) {
  const config = state.config;
  const node = state.nodes[nodeId];
  if (!node) return { note: 'timer for unknown node; ignored' };
  const role = node.durable.role;
  const t = event.payload.timerType;
  if (role === 'viewserver' && t === TIMER_CHECK) {
    return viewServerCheck(state, config, nodeId);
  }
  if (role === 'pbserver' && t === TIMER_PING) {
    return serverPingTimer(state, config, nodeId);
  }
  if (role === 'pbserver' && t === TIMER_OP) {
    return serverOpTimer(state, config, nodeId, event);
  }
  if (role === 'pbserver' && t === TIMER_TRANSFER) {
    return serverTransferTimer(state, config, nodeId, event);
  }
  if (role === 'pbclient' && t === TIMER_RETRY) {
    return clientRetry(state, config, nodeId, event);
  }
  return { note: 'unknown timer; ignored' };
}

export function checkInvariants(state) {
  return checkLab2Invariants(state);
}

export function describeEvent(event) {
  const p = event.payload || {};
  if (event.kind === EventKind.MESSAGE) {
    switch (p.msgType) {
      case MSG_PING:
        return 'deliver ping from ' + p.server;
      case MSG_VIEW:
        return 'deliver view ' + (p.view && p.view.n);
      case MSG_VIEW_QUERY:
        return 'deliver view query';
      case MSG_TRANSFER:
        return 'deliver state transfer for view ' + p.view;
      case MSG_TRANSFER_REPLY:
        return 'deliver transfer reply for view ' + p.view;
      case MSG_FORWARD:
        return 'deliver forward op ' + p.opnum;
      case MSG_FORWARD_REPLY:
        return 'deliver forward reply op ' + p.opnum;
      case MSG_CREQUEST:
        return 'deliver client request ' + p.client + '#' + p.seq;
      case MSG_CREPLY:
        return 'deliver client reply ' + p.client + '#' + p.seq;
      case MSG_STALE:
        return 'deliver stale-view redirect';
      default:
        return 'deliver message';
    }
  }
  if (event.kind === EventKind.TIMER) return 'fire timer ' + (p.timerKey || '');
  if (event.kind === EventKind.CLIENT_COMMAND) return 'invoke command';
  return event.label || event.kind;
}

export function summarizeNode(nodeId, node) {
  const { durable } = node;
  if (durable.role === 'viewserver') {
    const fmt = (v) =>
      v ? 'v' + v.n + ' P:' + (v.primary || '-') + ' B:' + (v.backup || '-') : 'none';
    return [
      ['role', 'view server'],
      ['current', fmt(durable.current)],
      ['pending', fmt(durable.pending)],
      [
        'last ping',
        Object.keys(durable.lastPing)
          .map((s) => s + '@' + durable.lastPing[s])
          .join(', ') || '(none)',
      ],
    ];
  }
  if (durable.role === 'pbserver') {
    const v = durable.view;
    return [
      ['role', 'replica'],
      ['view', 'v' + v.n + ' P:' + (v.primary || '-') + ' B:' + (v.backup || '-')],
      ['status', durable.status],
      ['keys', Object.keys(durable.kv).join(', ') || '(empty)'],
      ['applied', String(durable.appliedIds.length)],
      ['served', String(durable.served.length)],
      ['pending op', durable.pendingOp ? 'op ' + durable.pendingOp.opnum : 'none'],
    ];
  }
  const v = durable.view;
  return [
    ['role', 'client'],
    ['view', 'v' + v.n + ' P:' + (v.primary || '-')],
    ['completed', String(durable.completed.length)],
    ['outstanding', node.volatile.outstanding ? '#' + node.volatile.outstanding.seq : 'none'],
  ];
}


export function sampleCommands(config) {
  const client = config.clients[0];
  return [
    { label: 'PUT x = 1', to: client, command: { op: 'put', key: 'x', value: 1 } },
    { label: 'PUT y = 2', to: client, command: { op: 'put', key: 'y', value: 2 } },
    { label: 'GET x', to: client, command: { op: 'get', key: 'x' } },
    { label: 'APPEND x + "!"', to: client, command: { op: 'append', key: 'x', value: '!' } },
  ];
}

const lab2Adapter = {
  initialize,
  handleMessage,
  handleClientCommand,
  handleTimer,
  freshVolatile,
  checkInvariants,
  describeEvent,
  summarizeNode,
};

export default lab2Adapter;
